/* IMPORT BASE STYLE FIRST! */
import type { SliderProps } from 'rc-slider';
import React, { cloneElement } from 'react';
import Slider from 'rc-slider';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import raf from 'rc-util/lib/raf';
import styles from './range-slider.module.scss';

const SLIDER_HANDLE_ID = 'slider-handle-tooltip';

const HandleTooltip = (props: {
  value: number;
  children: React.ReactElement;
  visible: boolean;
  tipFormatter?: (value: number) => React.ReactNode;
}) => {
  const { value, children, visible } = props;

  const tooltipRef = React.useRef<any>();
  const rafRef = React.useRef<number | null>(null);

  function cancelKeepAlign() {
    if (rafRef.current) {
      raf.cancel(rafRef.current);
    }
  }

  function keepAlign() {
    rafRef.current = raf(() => {
      tooltipRef.current?.forceAlign();
    });
  }

  React.useEffect(() => {
    if (visible) {
      keepAlign();
    } else {
      cancelKeepAlign();
    }

    return cancelKeepAlign;
  }, [value, visible]);

  const sliderHandleClone = cloneElement(children, { id: SLIDER_HANDLE_ID });

  return (
    <>
      <ReactTooltip
        anchorSelect={`#${SLIDER_HANDLE_ID}`}
        place={'bottom'}
        noArrow
        offset={4}
        style={{ padding: '2px 6px', borderRadius: 1 }}
      >
        <div className="whitespace-pre">
          <span className="stihl-text-body-default-reg">{props.tipFormatter?.(value)}</span>
        </div>
      </ReactTooltip>{' '}
      {sliderHandleClone}
    </>
  );
};

export interface RangeSliderProps extends SliderProps<number> {
  tooltipFormatter?: (value: number) => string;
  legendFormatter?: (value: number) => string;
  onBlur?: () => void;
  tooltip?: boolean;
  legend?: boolean;
}

export const RangeSlider = ({
  disabled = false,
  legend = false,
  onChange,
  max,
  min,
  value,
  tooltip = false,
  tooltipFormatter,
  legendFormatter,
  ...restProps
}: RangeSliderProps) => {
  if ((!!value && !!max && value > max) || (!!value && !!min && value < min)) {
    console.error('Invalid value, provided value is outside the range!');
    return null;
  }

  const handleRenderer: SliderProps['handleRender'] = (node, handleProps) => {
    if (!tooltip) return node;

    return (
      <HandleTooltip value={handleProps.value} visible={handleProps.dragging} tipFormatter={tooltipFormatter}>
        {node}
      </HandleTooltip>
    );
  };

  const createDotStyle = (dotPosition: number) => {
    const radius = 4;
    const background = disabled || (value !== undefined && dotPosition > value) ? '#B1B1B1' : '#D9480F';
    const styles = { height: radius, width: radius, bottom: -1, border: 0, background };
    if (dotPosition === min) {
      return { ...styles, left: '1%' };
    }
    if (dotPosition === max) {
      return { ...styles, left: '99%' };
    }
    return styles;
  };

  return (
    <div>
      <Slider
        {...restProps}
        disabled={disabled}
        min={min}
        max={max}
        value={value}
        dotStyle={createDotStyle}
        classNames={{
          rail: styles.rangeSliderBase,
          track: disabled ? styles.rangeSliderProgressedDisabled : styles.rangeSliderProgressed,
          handle: styles.rangeSliderHandle,
        }}
        onChange={!disabled ? onChange : undefined}
        handleRender={handleRenderer}
      />
      {legend && (
        <div className="mb-4 mt-3 flex w-full justify-between">
          <span className="stihl-text-body-default-reg">{legendFormatter?.(min as number) ?? min}</span>
          <span className="stihl-text-body-default-reg">{legendFormatter?.(max as number) ?? max}</span>
        </div>
      )}
    </div>
  );
};
